import { createReducer, on } from '@ngrx/store';
import { BundleUpdateProcessTypeEnum, XotaBundleUpdateStep } from '@vi-iot/client/index';
import { FirmwareUpdateActions } from './firmware-update.actions';
import { FirmwareUpdateStep } from './firmware-update.model';

import {
  BundleUpdateProgressEnum,
  BundleVersion,
} from '../../../projects/client/apis/bundle-update/bundle-update.model';
import { FirmwareUpdateState } from './firmware-update.state';

export const initFirmwareUpdateState: FirmwareUpdateState = {
  step: null,
  bundleUpdateStep: null,
  processType: null,
  status: null,
  progress: null,
  currentBundleVersion: null,
  targetBundleVersion: null,
  updateIsAvailable: null,
  loading: false,
  checkUpdateFailure: false,
  isInUpdateTimeBlock: false,
  overallProgress: 0,
  componentVerification: null,
  firmwareUpdateVerification: null,
  missingBusTopology: null,
  salesForceCaseNumber: null,
  error: null,
  xotaOperationStatus: {
    tcuStatus: null,
    updateMethod: null,
    containerStatus: null,
    transferStatus: null,
    updateStatus: null,
    reportStatus: null,
  },
  gatewayVerification: {
    error: null,
    loading: false,
    value: null,
  },
};

export const firmwareUpdateReducer = createReducer(
  initFirmwareUpdateState,
  on(
    FirmwareUpdateActions.setFirmwareUpdateStep,
    (state, { step }): FirmwareUpdateState => ({ ...state, step })
  ),

  on(
    FirmwareUpdateActions.onCloseFirmwareUpdateModal,
    (): FirmwareUpdateState => initFirmwareUpdateState
  ),

  on(
    FirmwareUpdateActions.checkUpdateTimeBlock,
    (state): FirmwareUpdateState => ({ ...state, loading: true })
  ),

  on(
    FirmwareUpdateActions.checkUpdateTimeBlockSuccess,
    (state, { isInUpdateTimeBlock }): FirmwareUpdateState => ({
      ...state,
      isInUpdateTimeBlock,
      step: FirmwareUpdateStep.START,
      processType: BundleUpdateProcessTypeEnum.CheckForBundleUpdate,
      loading: false,
    })
  ),

  on(
    FirmwareUpdateActions.checkUpdateTimeBlockError,
    (state): FirmwareUpdateState => ({
      ...state,
      loading: false,
      step: FirmwareUpdateStep.START,
      isInUpdateTimeBlock: false,
    })
  ),

  on(
    FirmwareUpdateActions.checkUpdateVerification,
    (state): FirmwareUpdateState => ({
      ...state,
      loading: true,
    })
  ),

  on(
    FirmwareUpdateActions.checkUpdateVerificationSuccess,
    (state, { response }): FirmwareUpdateState => ({
      ...state,
      componentVerification: {
        legalApproved: response.data.legalApproved,
        wifiSignalEnough: response.data.wifiSignalEnough,
        diagStackInstancesAvailable: response.data.diagStackInstancesAvailable,
        oneTimeLegalApproved: response.data.oneTimeLegalApproved,
      },
      step: FirmwareUpdateStep.VERIFICATION,
      loading: false,
    })
  ),

  on(
    FirmwareUpdateActions.checkUpdateVerificationError,
    (state, { error }): FirmwareUpdateState => ({
      ...state,
      error,
      step: FirmwareUpdateStep.VERIFICATION,
      loading: false,
    })
  ),

  on(
    FirmwareUpdateActions.openOneTimeServiceApproval,
    (state): FirmwareUpdateState => ({
      ...state,
      step: FirmwareUpdateStep.ONE_TIME_APPROVAL,
    })
  ),

  on(
    FirmwareUpdateActions.checkingSalesForceCodeForCheckProcess,
    (state, { salesForceCaseNumber }): FirmwareUpdateState => ({
      ...state,
      salesForceCaseNumber,
      loading: true,
      error: null,
    })
  ),

  on(
    FirmwareUpdateActions.checkingSalesForceCodeForCheckProcessSuccess,
    (state, { response }): FirmwareUpdateState => ({
      ...state,
      step: FirmwareUpdateStep.VERIFICATION,
      componentVerification: {
        ...state.componentVerification,
        oneTimeLegalApproved: response.data.oneTimeLegalApproved,
      },
      error: null,
      loading: false,
    })
  ),
  on(
    FirmwareUpdateActions.checkingSalesForceCodeForCheckProcessError,
    (state, { error }): FirmwareUpdateState => ({
      ...state,
      error,
      loading: false,
    })
  ),

  on(
    FirmwareUpdateActions.checkingSalesForceCodeForUpdateProcess,
    (state, { salesForceCaseNumber }): FirmwareUpdateState => ({
      ...state,
      salesForceCaseNumber,
      processType: BundleUpdateProcessTypeEnum.BundleUpdate,
      overallProgress: 0,
      loading: true,
      error: null,
    })
  ),

  on(
    FirmwareUpdateActions.checkingSalesForceCodeForUpdateProcessSuccess,
    (state, { response }): FirmwareUpdateState => ({
      ...state,
      bundleUpdateStep: XotaBundleUpdateStep.UPDATING,
      componentVerification: {
        ...state.componentVerification,
        oneTimeLegalApproved: response.data.oneTimeLegalApproved,
      },
      error: null,
      loading: false,
    })
  ),
  on(
    FirmwareUpdateActions.checkingSalesForceCodeForUpdateProcessError,
    (state, { error }): FirmwareUpdateState => ({
      ...state,
      error,
      step: FirmwareUpdateStep.VERIFICATION,
      loading: false,
    })
  ),

  on(
    FirmwareUpdateActions.setOverallProgress,
    (state): FirmwareUpdateState => ({
      ...state,
      step: FirmwareUpdateStep.LOADING,
      loading: true,
    })
  ),

  on(
    FirmwareUpdateActions.setOverallProgressSuccess,
    (state, { bundleUpdateStatusResponse }): FirmwareUpdateState => {
      const data = bundleUpdateStatusResponse.data;

      return {
        ...state,
        ...data,
        missingBusTopology: {
          missingTcu63InBusTopology: data.missingTcu63InBusTopology,
          missingEcusInTopology: data.missingEcusInTopology,
          othersEcusMissingInBusTopology: data.othersEcusMissingInBusTopology,
        },
        xotaOperationStatus: {
          tcuStatus: data.tcuStatus,
          updateMethod: data.updateMethod,
          containerStatus: data.containerStatus,
          transferStatus: data.transferStatus,
          updateStatus: data.updateStatus,
          reportStatus: data.reportStatus,
        },
        loading: false,
      };
    }
  ),

  on(
    FirmwareUpdateActions.setOverallProgressError,
    (state, { error }): FirmwareUpdateState => ({
      ...state,
      error,
      loading: false,
    })
  ),

  on(
    FirmwareUpdateActions.setOverallProgressFirmwareUpdate,
    (state): FirmwareUpdateState => ({
      ...state,
      step: FirmwareUpdateStep.PREPARATION,
      bundleUpdateStep: XotaBundleUpdateStep.UPDATING,
      loading: true,
    })
  ),

  on(
    FirmwareUpdateActions.setOverallProgressFirmwareUpdateSuccess,
    (state, { bundleUpdateStatusResponse, gatewaySerial }): FirmwareUpdateState => {
      const data = bundleUpdateStatusResponse.data;

      // always get from local due to the currentBundleVersion is always null in the endpoint /status/
      const localStorageUpdateCurrentBundleVersion = getCurrentBundleVersionFromLocal(
        gatewaySerial
      );

      return {
        ...state,
        ...data,
        bundleUpdateStep:
          data.progress === BundleUpdateProgressEnum.Finished
            ? XotaBundleUpdateStep.FINALIZE
            : XotaBundleUpdateStep.UPDATING,
        currentBundleVersion: localStorageUpdateCurrentBundleVersion,
        xotaOperationStatus: {
          tcuStatus: data.tcuStatus,
          updateMethod: data.updateMethod,
          containerStatus: data.containerStatus,
          transferStatus: data.transferStatus,
          updateStatus: data.updateStatus,
          reportStatus: data.reportStatus,
        },
        loading: true,
      };
    }
  ),

  on(
    FirmwareUpdateActions.setOverallProgressFirmwareUpdateError,
    (state, { error }): FirmwareUpdateState => ({
      ...state,
      error,
      loading: false,
    })
  ),

  on(
    FirmwareUpdateActions.showUpToDate,
    (state): FirmwareUpdateState => ({
      ...state,
      loading: false,
      step: FirmwareUpdateStep.UP_TO_DATE,
    })
  ),

  on(
    FirmwareUpdateActions.showFirmwareBundleUpdate,
    (state, { bundleUpdateStatusResponse }): FirmwareUpdateState => {
      const data = bundleUpdateStatusResponse.data;

      return {
        ...state,
        ...data,
        step: FirmwareUpdateStep.PREPARATION,
        bundleUpdateStep: XotaBundleUpdateStep.CHECK_FOR_UPDATE,
        missingBusTopology: {
          missingTcu63InBusTopology: data.missingTcu63InBusTopology,
          missingEcusInTopology: data.missingEcusInTopology,
          othersEcusMissingInBusTopology: data.othersEcusMissingInBusTopology,
        },
        xotaOperationStatus: {
          tcuStatus: data.tcuStatus,
          updateMethod: data.updateMethod,
          containerStatus: data.containerStatus,
          transferStatus: data.transferStatus,
          updateStatus: data.updateStatus,
          reportStatus: data.reportStatus,
        },
        loading: false,
      };
    }
  ),

  on(
    FirmwareUpdateActions.checkUpdateFirmwareVerification,
    (state): FirmwareUpdateState => ({
      ...state,
      loading: true,
    })
  ),

  on(
    FirmwareUpdateActions.checkUpdateFirmwareVerificationSuccess,
    (state, { response }): FirmwareUpdateState => ({
      ...state,
      componentVerification: {
        legalApproved: response.data.legalApproved,
        wifiSignalEnough: response.data.wifiSignalEnough,
        diagStackInstancesAvailable: response.data.diagStackInstancesAvailable,
        oneTimeLegalApproved: response.data.oneTimeLegalApproved,
      },
      loading: false,
    })
  ),

  on(
    FirmwareUpdateActions.checkUpdateFirmwareVerificationError,
    (state, { error }): FirmwareUpdateState => ({
      ...state,
      error,
      step: FirmwareUpdateStep.VERIFICATION,
      loading: false,
    })
  ),

  on(
    FirmwareUpdateActions.triggerUpdateFirmware,
    (state): FirmwareUpdateState => ({
      ...state,
      overallProgress: 0,
      step: FirmwareUpdateStep.PREPARATION,
      bundleUpdateStep: XotaBundleUpdateStep.UPDATING,
      loading: true,
    })
  ),

  on(
    FirmwareUpdateActions.xota1showFirmwareBundleAfterUpdate,
    (state, { bundleUpdateStatusResponse, gatewaySerial }): FirmwareUpdateState => {
      const data = bundleUpdateStatusResponse.data;

      const localStorageUpdateCurrentBundleVersion = getCurrentBundleVersionFromLocal(
        gatewaySerial
      );

      // requested for keeping the same up-to-date behavior for the ecuName that up-to-date
      // from check endpoint while keeping the latest versions for the rest
      const afterUpdateCurrentBundleVersion = data.currentBundleVersion;
      localStorageUpdateCurrentBundleVersion.forEach((target) => {
        if (!target.otaVersions || target.otaVersions.length === 0) {
          const index = afterUpdateCurrentBundleVersion.findIndex(
            (item) => item.ecuName === target.ecuName
          );
          if (index !== -1) {
            afterUpdateCurrentBundleVersion[index] = target;
          }
        }
      });

      return {
        ...state,
        ...data,
        bundleUpdateStep: XotaBundleUpdateStep.FINALIZE,
        currentBundleVersion: afterUpdateCurrentBundleVersion,
        missingBusTopology: {
          missingTcu63InBusTopology: data.missingTcu63InBusTopology,
          missingEcusInTopology: data.missingEcusInTopology,
          othersEcusMissingInBusTopology: data.othersEcusMissingInBusTopology,
        },
        xotaOperationStatus: {
          tcuStatus: data.tcuStatus,
          updateMethod: data.updateMethod,
          containerStatus: data.containerStatus,
          transferStatus: data.transferStatus,
          updateStatus: data.updateStatus,
          reportStatus: data.reportStatus,
        },
        loading: false,
      };
    }
  ),
  on(
    FirmwareUpdateActions.xota2showProcessStatusAfterUpdate,
    (state, { bundleUpdateStatusResponse }): FirmwareUpdateState => ({
      ...state,
      ...bundleUpdateStatusResponse.data,
      bundleUpdateStep: XotaBundleUpdateStep.FINALIZE,
      xotaOperationStatus: {
        tcuStatus: bundleUpdateStatusResponse.data.tcuStatus,
        updateMethod: bundleUpdateStatusResponse.data.updateMethod,
        containerStatus: bundleUpdateStatusResponse.data.containerStatus,
        transferStatus: bundleUpdateStatusResponse.data.transferStatus,
        updateStatus: bundleUpdateStatusResponse.data.updateStatus,
        reportStatus: bundleUpdateStatusResponse.data.reportStatus,
      },
      loading: false,
    })
  ),
  on(
    FirmwareUpdateActions.verifyGateway.attempt,
    (state): FirmwareUpdateState => ({
      ...state,
      gatewayVerification: { error: null, loading: true, value: null },
    })
  ),
  on(
    FirmwareUpdateActions.verifyGateway.errror,
    (state, { error }): FirmwareUpdateState => ({
      ...state,
      gatewayVerification: { ...state.gatewayVerification, error, loading: false },
    })
  ),
  on(
    FirmwareUpdateActions.verifyGateway.success,
    (state, { value }): FirmwareUpdateState => ({
      ...state,
      gatewayVerification: { ...state.gatewayVerification, loading: false, value },
    })
  )
);

function getCurrentBundleVersionFromLocal(gatewaySerial: string): BundleVersion[] {
  const currentBundleVersionKey = 'currentBundleVersion';

  const localCurrentVersionBundleKey = `${currentBundleVersionKey}-${gatewaySerial}`;
  const localStorageUpdateCurrentBundleVersion = JSON.parse(
    localStorage.getItem(localCurrentVersionBundleKey)
  );

  return localStorageUpdateCurrentBundleVersion;
}
